/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import { domUtils } from '@ba-ui-toolkit/utils';

const { IE11, getTransformedParentElement } = domUtils;

// Checks if all placements have been tried
function areRemainingPlacements(placementHistory) {
	if (
		placementHistory.indexOf('top') >= 0 &&
		placementHistory.indexOf('bottom') >= 0 &&
		placementHistory.indexOf('left') >= 0 &&
		placementHistory.indexOf('right') >= 0
	) {
		return false;
	}

	return true;
}

function getStartingOffset(triggerRectOffset, nearestRectOffset, scroll, positionedElement) {
	return triggerRectOffset - nearestRectOffset - (positionedElement ? 0 : scroll);
}

const invalidSpecs = { top: 0, left: 0, placement: 'right', invalid: true };

// Returns the actual result specs to use once placement is determined for sure
export function getResultSpecs(
	placement,
	listDropdownTrigger,
	triggerRect,
	containerRect,
	scrollTop,
	scrollLeft,
	options
) {
	let resultTop;
	let resultLeft;

	let arrowRect;
	if (options.arrow) arrowRect = options.arrow.getBoundingClientRect();

	const nearestPositionedElement = !IE11 && getTransformedParentElement(listDropdownTrigger);
	const nearestRect = (nearestPositionedElement || document.body).getBoundingClientRect();

	switch (placement) {
		case 'top':
			resultTop =
				getStartingOffset(
					triggerRect.top,
					nearestRect.top,
					scrollTop,
					nearestPositionedElement
				) - containerRect.height;
			resultLeft =
				getStartingOffset(
					triggerRect.left,
					nearestRect.left,
					scrollLeft,
					nearestPositionedElement
				) + triggerRect.width;
			resultLeft -= triggerRect.width;
			if (arrowRect) resultTop -=
					arrowRect.height < arrowRect.width ? arrowRect.height : arrowRect.width;
			if (typeof options.offset !== 'undefined') resultTop -= options.offset;
			break;
		case 'bottom':
			resultTop =
				getStartingOffset(
					triggerRect.top,
					nearestRect.top,
					scrollTop,
					nearestPositionedElement
				) + triggerRect.height;
			resultLeft =
				getStartingOffset(
					triggerRect.left,
					nearestRect.left,
					scrollLeft,
					nearestPositionedElement
				) + triggerRect.width;
			resultLeft -= triggerRect.width;
			if (arrowRect) resultTop +=
					arrowRect.height < arrowRect.width ? arrowRect.height : arrowRect.width;
			if (typeof options.offset !== 'undefined') resultTop += options.offset;
			break;
		case 'left':
			resultTop = getStartingOffset(
				triggerRect.top,
				nearestRect.top,
				scrollTop,
				nearestPositionedElement
			);
			resultLeft =
				getStartingOffset(triggerRect.left, nearestRect.left, scrollLeft) -
				containerRect.width;
			if (arrowRect) resultLeft -=
					arrowRect.height < arrowRect.width ? arrowRect.height : arrowRect.width;
			if (typeof options.offset !== 'undefined') resultLeft -= options.offset;
			break;
		case 'right':
			resultTop = getStartingOffset(
				triggerRect.top,
				nearestRect.top,
				scrollTop,
				nearestPositionedElement
			);
			resultLeft =
				getStartingOffset(triggerRect.left, nearestRect.left, scrollLeft) +
				triggerRect.width;
			if (arrowRect) resultLeft +=
					arrowRect.height < arrowRect.width ? arrowRect.height : arrowRect.width;
			if (typeof options.offset !== 'undefined') resultLeft += options.offset;
			break;
		default:
			return invalidSpecs;
	}

	return { top: Math.round(resultTop), left: Math.round(resultLeft), placement, invalid: false };
}

export function getValidPlacement(
	startingTop,
	startingLeft,
	heightToCheck,
	widthToCheck,
	placement,
	triggerRect,
	containerRect,
	options,
	arrowRect
) {
	let placementIsValid = false;
	const placementHistory = [];

	let checkTop;
	let checkLeft;

	while (!placementIsValid) {
		checkTop = startingTop;
		checkLeft = startingLeft;

		if (!areRemainingPlacements(placementHistory)) {
			return false;
		}

		switch (placement) {
			case 'top':
				checkTop -= containerRect.height;
				if (arrowRect) checkTop -= arrowRect.height;
				if (typeof options.offset !== 'undefined') checkTop -= options.offset;
				checkLeft += triggerRect.width - containerRect.width;

				if (checkTop > 0 && checkLeft > 0) {
					placementIsValid = true;
				} else if (placementHistory.indexOf('bottom') < 0) {
					placement = 'bottom';
				} else {
					placement = 'right';
				}
				placementHistory.push('top');
				break;
			case 'bottom':
				checkTop += triggerRect.height;
				if (arrowRect) checkTop += arrowRect.height;
				if (typeof options.offset !== 'undefined') checkTop += options.offset;
				checkLeft += triggerRect.width - containerRect.width;

				if (heightToCheck > checkTop + containerRect.height && checkLeft > 0) {
					placementIsValid = true;
				} else if (placementHistory.indexOf('top') < 0) {
					placement = 'top';
				} else {
					placement = 'right';
				}
				placementHistory.push('bottom');
				break;
			case 'left':
				checkLeft -= containerRect.width;
				if (arrowRect) checkLeft -= arrowRect.width;
				if (typeof options.offset !== 'undefined') checkLeft -= options.offset;

				if (checkLeft > 0 && heightToCheck > checkTop + containerRect.height) {
					placementIsValid = true;
				} else if (placementHistory.indexOf('right') < 0) {
					placement = 'right';
				} else {
					placement = 'top';
				}
				placementHistory.push('left');
				break;
			case 'right':
				checkLeft += triggerRect.width;
				if (arrowRect) checkLeft += arrowRect.width;
				if (typeof options.offset !== 'undefined') checkLeft += options.offset;

				if (
					widthToCheck > checkLeft + containerRect.width &&
					heightToCheck > checkTop + containerRect.height
				) {
					placementIsValid = true;
				} else if (placementHistory.indexOf('left') < 0) {
					placement = 'left';
				} else {
					placement = 'top';
				}
				placementHistory.push('right');
				break;
			default:
				return false;
		}
	}

	return placement;
}

// Determines the position for listDropdown, while finding a valid placement and checking if there is space to place the listDropdown
export default function getSpecs(
	listDropdownTrigger,
	listDropdownContainer,
	placement,
	options = {}
) {
	if (
		!listDropdownTrigger ||
		!listDropdownContainer ||
		listDropdownTrigger.getBoundingClientRect === undefined ||
		listDropdownContainer.getBoundingClientRect === undefined
	) {
		return invalidSpecs;
	}

	const triggerRect = listDropdownTrigger.getBoundingClientRect();
	const containerRect = listDropdownContainer.getBoundingClientRect();
	const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	const { body } = document;
	const html = document.documentElement;
	const pageHeight = Math.max(
		body.scrollHeight,
		body.offsetHeight,
		html.clientHeight,
		html.offsetHeight
	);
	const pageWidth = Math.max(
		body.scrollWidth,
		body.offsetWidth,
		html.clientWidth,
		html.offsetWidth
	);

	let arrowRect;
	if (options.arrow) arrowRect = options.arrow.getBoundingClientRect();

	// Check if a valid placement that fits in the current window the page exists
	let validPlacement = getValidPlacement(
		triggerRect.top,
		triggerRect.left,
		window.innerHeight,
		window.innerWidth,
		placement,
		triggerRect,
		containerRect,
		options,
		arrowRect
	);
	// If no valid placement fits in the current window, check if there exists a valid placement on the page (i.e. will not be fully visible in the window, but will fit on the page)
	if (!validPlacement) {
		validPlacement = getValidPlacement(
			triggerRect.top + scrollTop,
			triggerRect.left + scrollLeft,
			pageHeight,
			pageWidth,
			placement,
			triggerRect,
			containerRect,
			options,
			arrowRect
		);

		// If no room on the page to fit, then return as invalid
		if (!validPlacement) return invalidSpecs;
	}

	return getResultSpecs(
		validPlacement,
		listDropdownTrigger,
		triggerRect,
		containerRect,
		scrollTop,
		scrollLeft,
		options
	);
}
